import { useEffect, useRef } from 'react'
import { AppointmentType, EventType } from '../../constants'
import { uniq } from '../../util/fp'
import { TrialStatusSource } from '../useTrialGate'
import { getTrialGate, TrialGateSurface } from '../useTrialGate/trialGates'

type UseTrialGateTrackingParams = {
  /**
   * Every direction whose flag is on, not just the blocked one. Unknown trial data blocks nothing,
   * so the fallback is reported per switched-on direction instead. Both fire when both flags are
   * on, which is accurate: each feature independently failed to reach a decision for that session.
   */
  activeTrialGateTypes: AppointmentType[]
  /** Every type this customer's trial status blocks - out of trial, that is more than one. */
  blockedAppointmentTypes: AppointmentType[]
  isTrialDataUnknown: boolean
  /**
   * The appointment types this surface actually puts in front of the user. Both events are reported
   * only for types present here, so the two metrics share a denominator: an HAE-restricted journey
   * or a delivery model without Aftercare Follow-up records neither a disabled impression nor a
   * fallback for a type the provider could never have picked.
   */
  offeredAppointmentTypes: AppointmentType[]
  /**
   * Which surface is reporting. Both events below are per-surface counts, and one session can
   * traverse all three, so the payload has to say which one or they collapse into one inflated
   * number in Mixpanel (PX-3765).
   */
  surface: TrialGateSurface
  trackEvent: (eventType: EventType, payload?: { data?: Record<string, unknown> }) => void
  trialEndDate: Date | null
  trialStatusSource?: TrialStatusSource
}

/**
 * The trial-gate analytics, shared by the three surfaces that can show a blocked appointment type
 * (the appointment-type selection screen, the schedule form, and the consultant route's
 * configuration panel). Each surface records its own impression, so these are per-surface counts,
 * not per-session ones: New/index.tsx swaps the selection screen for the form once a type is picked,
 * and a session that traverses both records two. Reading them as unique sessions overcounts wherever
 * ENABLE_APPOINTMENT_TYPE_SELECTION routes users through the selection screen first. Every surface
 * reports because a session that reaches one of the forms directly would otherwise record nothing at
 * all.
 *
 * Takes the trial-gate values and `trackEvent` as arguments rather than calling the hooks itself, so
 * each surface keeps ownership of its own data flow and this stays a pure effects helper.
 */
const useTrialGateTracking = ({
  activeTrialGateTypes,
  blockedAppointmentTypes,
  isTrialDataUnknown,
  offeredAppointmentTypes,
  surface,
  trackEvent,
  trialEndDate,
  trialStatusSource,
}: UseTrialGateTrackingParams): void => {
  // useEventTracking hands back a new trackEvent identity on every render; stash it in a ref so
  // these effects' real dependencies stay exhaustive without re-firing on every render.
  const trackEventRef = useRef(trackEvent)
  trackEventRef.current = trackEvent

  // The effects below depend on these joined strings rather than the arrays, which the caller
  // rebuilds every render. Both effects dedupe against a ref, so a re-run on an unchanged value is a
  // no-op anyway - the key is what stops it running at all.
  const impressionTypesKey = blockedAppointmentTypes.filter((type) => offeredAppointmentTypes.includes(type)).join(',')

  // One fallback per *distinct event name*, not per type: Follow-up and Hearing Aid Evaluation share
  // a fallback event by design (one flag, one decision), so reporting per type would count the same
  // unresolved session twice. The impression events stay per type, because each blocked option is
  // its own thing the provider saw.
  const fallbackEventsKey = isTrialDataUnknown
    ? uniq(
        activeTrialGateTypes
          .filter((type) => offeredAppointmentTypes.includes(type))
          .flatMap((type) => getTrialGate(type)?.validationFallbackEvent ?? [])
      ).join('|')
    : ''

  // A direction already reported *within this surface's mount* must not report again if the set
  // merely grows - LaunchDarkly can stream the second flag on while the page is open, which changes
  // the key and would otherwise re-send the first direction's event. Deliberately not shared across
  // surfaces: like the impression event above, this counts per surface (see the note on the hook),
  // so the two trial-gate metrics stay on the same axis and remain comparable.
  //
  // Narrower than the mount even so - it is cleared on the way out of an unknown-data episode below,
  // because the opportunity query can error, recover, and error again, and that second failure is a
  // genuinely new fallback worth counting.
  const reportedFallbackEventsRef = useRef(new Set<EventType>())

  /**
   * What has already been reported within this surface's mount, so a blocked set that merely grows
   * does not re-send what it already sent - LaunchDarkly can stream a flag on while the page is
   * open. The key carries the trial data with it, so a genuine change in the customer's trial status
   * reports again rather than being swallowed as a duplicate.
   */
  const reportedImpressionsRef = useRef(new Set<string>())

  // The ISO string, not the Date: useTrialGate re-parses trialEndDate on every render, so the Date
  // instance changes identity even when the value has not. Depending on the object would re-fire the
  // effect below on every render - and useNow refetches every 60s with refetchIntervalInBackground,
  // so a screen left open would emit the event indefinitely.
  const trialEndDateIso = trialEndDate?.toISOString()

  useEffect(() => {
    if (!impressionTypesKey) return
    ;(impressionTypesKey.split(',') as AppointmentType[]).forEach((type) => {
      const disabledOptionEvent = getTrialGate(type)?.disabledOptionEvent
      if (!disabledOptionEvent) return
      const impressionKey = `${type}|${trialEndDateIso}|${trialStatusSource}`
      if (reportedImpressionsRef.current.has(impressionKey)) return
      reportedImpressionsRef.current.add(impressionKey)
      trackEventRef.current(disabledOptionEvent, {
        data: { surface, trialEndDate: trialEndDateIso, trialStatusSource },
      })
    })
  }, [impressionTypesKey, surface, trialEndDateIso, trialStatusSource])

  useEffect(() => {
    if (!isTrialDataUnknown) {
      reportedFallbackEventsRef.current.clear()
      return
    }
    ;(fallbackEventsKey ? (fallbackEventsKey.split('|') as EventType[]) : []).forEach((event) => {
      if (reportedFallbackEventsRef.current.has(event)) return
      reportedFallbackEventsRef.current.add(event)
      trackEventRef.current(event, { data: { surface } })
    })
  }, [isTrialDataUnknown, fallbackEventsKey, surface])
}

export { useTrialGateTracking }
